import { useEffect, useRef } from 'react';
import { burstConfettiFromElement } from '../utils/confetti';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

type Params = {
  id: string;
  progress: number;
  element: HTMLElement | null;
  enabled?: boolean;
  sequenceIndex?: number;
  baseDelayMs?: number;
};

type TimerHandle = ReturnType<typeof setTimeout>;

export function useConfettiOnComplete({
	id,
	progress,
	element,
	enabled = true,
	sequenceIndex = 0,
	baseDelayMs = 300,
}: Params) {
	const reducedMotion = usePrefersReducedMotion();

	const cascadeDelayMs = 180;
	const firedRef = useRef<Record<string, boolean>>({});
	const timersRef = useRef<Record<string, TimerHandle | undefined>>({});

	useEffect(() => {
		if (!enabled || !element || reducedMotion) return undefined;
		if (progress < 1 || firedRef.current[id]) return undefined;

		const delay = baseDelayMs + sequenceIndex * cascadeDelayMs;

		const timers = timersRef.current;
		const key = id;

		const prev = timers[key];
		if (prev !== undefined) {
			clearTimeout(prev);
			timers[key] = undefined;
		}

		const t: TimerHandle = setTimeout(() => {
			firedRef.current[key] = true;

			burstConfettiFromElement(element);

			element.animate(
				[
					{ transform: 'scale(1)', boxShadow: '0 0 0 rgba(0,0,0,0)' },
					{ transform: 'scale(1.01)', boxShadow: '0 8px 24px rgba(17, 24, 39, 0.12)' },
					{ transform: 'scale(1)', boxShadow: '0 0 0 rgba(0,0,0,0)' },
				],
				{ duration: 450, easing: 'ease-out' }
			);

			timers[key] = undefined;
		}, delay);

		timers[key] = t;

		return () => {
			const handle = timers[key];
			if (handle !== undefined) {
				clearTimeout(handle);
				timers[key] = undefined;
			}
		};
	}, [enabled, element, reducedMotion, progress, id, sequenceIndex, baseDelayMs]);
}
